import isEmpty from 'lodash/isEmpty'
import { useMemo } from 'react'

import { PTC_FARE_CATEGORY, type PtcFareTypeCode } from '@/constants/components/fareType'
import { DEPARTURE_TIME_MAPPING_OBJ } from '@/constants/Srp'
import { Card, Cards, FareType, ListOfKeysType, SrpFareObject, SubTravelOptions } from '@/types/srp'
import { formatCurrency } from '@/utils'
import { getFormattedDate } from '@/utils/general/date'
import { isCheckInBaggageEnabled } from '@/utils/srp'
import { getFaresInfo } from '@/utils/srp/helper'

type DepartureTimeKey = keyof typeof DEPARTURE_TIME_MAPPING_OBJ

const resolvePtcFareType = (ptcFare?: { checked?: boolean; type?: string }): PtcFareTypeCode | '' => {
    if (!ptcFare?.checked) return ''
    if (ptcFare.type === 'senior_citizen' || ptcFare.type === 'army' || ptcFare.type === 'student') {
        return ptcFare.type
    }
    return 'student'
}
type useFilteredCardsProps = {
    cards: Cards
    selectedFilters: any
    isStoreDataLoading: boolean
    isRT: boolean
    subTravelOptions: SubTravelOptions
    fares: SrpFareObject
    isPerAdult: boolean
    filterData?: any
}

const useFilteredCards = ({
    cards,
    selectedFilters,
    isStoreDataLoading,
    isRT,
    subTravelOptions,
    fares,
    isPerAdult,
    filterData
}: useFilteredCardsProps) => {
    const listOfKeys: ListOfKeysType = isRT ? ['J1', 'J2'] : ['J1']
    const { ow: showCheckInBaggagePriceOW, rt: showCheckInBaggagePriceRT } = isCheckInBaggageEnabled(selectedFilters)

    const getFilteredCardsForJourney = (cards: Card[], journeyIndex: number, selectedFilters: any) => {
        try {
            const {
                stops = {},
                airlineFilter = {},
                departureTimeFrom = [],
                arrivalTimeFrom = [],
                nearbyAirportFilter = [],
                tripDuration={},
                layoverDuration={},
                oneWayPrice={},
                fareInclusions={},
                ctUpgrade= {},
                layoverAirport={},
                ptcFare={}
            } = journeyIndex === 0 ? selectedFilters.onwardLeg || {} : selectedFilters.returnLeg || {}

            const totalRTPriceFilter = isPerAdult ? selectedFilters?.totalRoundTripPrice?.currentADT : selectedFilters?.totalRoundTripPrice?.current
            const checkInBaggage = fareInclusions?.selectedValues?.includes('withBaggage')

            const showCheckInBaggagePrice = journeyIndex === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
            //change above - keep both or just new experiment
            if (isEmpty(cards)) {
                return []
            }

            return cards.filter(card => {
                let isCardValid = true

                // total round trip price filter
                if(totalRTPriceFilter && journeyIndex === 0) {
                   const cardTotalPrice = isPerAdult ? card?.summary?.lowestRoundTripPriceDetails?.perAdultPricing?.displayPrice : card?.summary?.lowestRoundTripPriceDetails?.totalPricing?.displayPrice
                   isCardValid = cardTotalPrice <= totalRTPriceFilter
                   if (!isCardValid) {
                    return isCardValid
                }
                }

                // stops filter
                const { selectedValues: selectedStops = [] } = stops || {}
                const stopsInCard = card?.summary?.stops
                if (!isEmpty(selectedStops)) {
                    isCardValid = selectedStops.includes(stopsInCard)
                    if (!isCardValid) {
                        return isCardValid
                    }
                }

                const faresObjectList = getFaresInfo(card, subTravelOptions, fares) || []

                // multi airline filter
                const { showMultiAirlineItineraries : shouldShowMultiAirline = true } = airlineFilter || {}
                if(!shouldShowMultiAirline) {
                    const airlinesInCard = card?.summary?.flights?.map(flightObj => flightObj?.airlineCode) || []
                    isCardValid= airlinesInCard.every(airline => airline === airlinesInCard[0]);
                    if (!isCardValid) {
                        return isCardValid
                    }
                }

                // airlines filter
                const { selectedValues: selectedAirlineValues } = airlineFilter || {}
                let isAirlineFound = false
                if (!isEmpty(selectedAirlineValues)) {
                    const airlinesInCard = card?.summary?.flights?.map(flightObj => flightObj.airlineCode) || []
                    airlinesInCard.forEach(airline => {
                        if (selectedAirlineValues.includes(airline)) {
                            isAirlineFound = true
                        }
                    })
                    isCardValid = isAirlineFound
                    if (!isCardValid) {
                        return isCardValid
                    }
                }

                // departure time filter
                // Helper to check if a flight time falls within any selected departure time ranges
                function isFlightInSelectedDepartureTimes(selectedValues: DepartureTimeKey[] = []) {
                    if (isEmpty(selectedValues)) return true;
                    const flightTime = parseInt(
                        getFormattedDate(card.summary.firstDeparture.airport.time, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
                            .replace(':', '')
                    );
                    return selectedValues.some((time: DepartureTimeKey) => {
                        const { time: timeObj } = DEPARTURE_TIME_MAPPING_OBJ[time];
                        return flightTime >= timeObj.start && flightTime <= timeObj.end;
                    });
                }

                // Handle departure time filter for onward and return legs
                const departureLegs = Array.isArray(departureTimeFrom) ? departureTimeFrom : [];
                for (let i = 0; i < departureLegs.length; i++) {
                    const selectedValues = departureLegs[i]?.selectedValues;
                    if (!isEmpty(selectedValues) && !isFlightInSelectedDepartureTimes(selectedValues)) {
                        return false;
                    }
                }

                // arrival time filter
                // Helper to check if a flight time falls within any selected arrival time ranges
                function isFlightInSelectedArrivalTimes(selectedValues: DepartureTimeKey[] = []) {
                    if (isEmpty(selectedValues)) return true;
                    const flightTime = parseInt(
                        getFormattedDate(card.summary.lastArrival.airport.time, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
                            .replace(':', '')
                    );
                    return selectedValues.some((time: DepartureTimeKey) => {
                        const { time: timeObj } = DEPARTURE_TIME_MAPPING_OBJ[time];
                        return flightTime >= timeObj.start && flightTime <= timeObj.end;
                    });
                }

                // Handle arrival time filter for onward and return legs
                const arrivalLegs = Array.isArray(arrivalTimeFrom) ? arrivalTimeFrom : [];
                for (let i = 0; i < arrivalLegs.length; i++) {
                    const selectedValues = arrivalLegs[i]?.selectedValues;
                    if (!isEmpty(selectedValues) && !isFlightInSelectedArrivalTimes(selectedValues)) {
                        return false;
                    }
                }

               
                if(!isEmpty(nearbyAirportFilter)) {
                    const { selectedValues: nearbyAirportSelectedValuesOnward = [] } = nearbyAirportFilter?.[0] || {}
                    const { selectedValues: nearbyAirportSelectedValuesReturn = []} = nearbyAirportFilter?.[1] || {}
                    const nearbyAirportSelectedValues = [...nearbyAirportSelectedValuesOnward, ...nearbyAirportSelectedValuesReturn]
                    const { firstDeparture, lastArrival } = card.summary
                    if(nearbyAirportFilter.length > 1) {
                        if(nearbyAirportSelectedValuesOnward.length > 0 && nearbyAirportSelectedValuesReturn.length > 0) {
                            isCardValid = nearbyAirportSelectedValuesOnward.includes(firstDeparture.airport.code) &&
                            nearbyAirportSelectedValuesReturn.includes(lastArrival.airport.code)
                        }
                        else if(nearbyAirportSelectedValuesOnward.length > 0) {
                            isCardValid = nearbyAirportSelectedValuesOnward.includes(firstDeparture.airport.code)
                        }
                        else if(nearbyAirportSelectedValuesReturn.length > 0) {
                            isCardValid = nearbyAirportSelectedValuesReturn.includes(lastArrival.airport.code)
                        }
                    }
                    else if(nearbyAirportSelectedValues.length > 0){
                        isCardValid =
                        nearbyAirportSelectedValues.includes(firstDeparture.airport.code) ||
                        nearbyAirportSelectedValues.includes(lastArrival.airport.code)
                    }
                    if(!isCardValid) {
                        return isCardValid;
                    }
                }

                // trip duration filter
                const { current: tripDurationCurrent } = tripDuration || {};
                if (!isEmpty(tripDurationCurrent)) {
                    const totalDurationInMinutes =
                     (card.summary.totalDuration.hh*60) + (card.summary.totalDuration.mm ); 
                    if (
                        totalDurationInMinutes < (tripDurationCurrent.min*60) ||
                        totalDurationInMinutes > (tripDurationCurrent.max*60)
                    ) {
                        return false;
                    }
                }

    
                //  layover duration filter
                const { current: layoverDurationCurrent } = layoverDuration || {};
                if (!isEmpty(layoverDurationCurrent)) {
                    const layoverDetails = card?.layover || []
                    let totalLayoverHours = 0
                    let totalLayoverMinutes = 0
                    layoverDetails.forEach((layover: any) => {
                        totalLayoverHours += layover?.totalDuration?.hh || 0
                        totalLayoverMinutes += layover?.totalDuration?.mm || 0
                    })
                    const layoverDurationInMinutes = (totalLayoverHours*60) + totalLayoverMinutes
                    if (
                        layoverDurationInMinutes < (layoverDurationCurrent.min*60) ||
                        layoverDurationInMinutes > (layoverDurationCurrent.max*60)
                    ) {
                        return false; 
                    }
                 }
                 //price filter
                 const { min: minPriceRange, minADT: minPriceRangeADT, current: selectedPrice, currentADT: selectedPriceADT } = oneWayPrice || {}
                 const minPrice = isPerAdult ? minPriceRangeADT : minPriceRange
                 const selectedPriceFinal = isPerAdult ? selectedPriceADT : selectedPrice
                 const cheapestFare = faresObjectList.find((fare: any) => fare?.isCheapest)
                 if (!isEmpty(oneWayPrice) && !isEmpty(cheapestFare)) {
                    const price =  isPerAdult ? cheapestFare?.pricing?.perAdultPricing?.strikeOffDetails?.displayPrice : cheapestFare?.pricing?.totalPricing?.strikeOffDetails?.displayPrice
                    isCardValid = price <= selectedPriceFinal
                    if (!isCardValid) {
                        return isCardValid
                    }  
                }        

                 //check in baggage filter
                if (checkInBaggage) {
                    let checkInBaggageAllowed = false
                    for (const fare of faresObjectList) {
                        checkInBaggageAllowed = fare?.checkInBaggageAllowed
                        if (checkInBaggageAllowed) break
                    }
                    if (!checkInBaggageAllowed) return false
                }

                if(ptcFare?.checked) {
                    const ptcType = resolvePtcFareType(ptcFare) || 'student'
                    const matchingPtcFare = faresObjectList?.find(
                        (fare = {}) => fare?.fareCategory === PTC_FARE_CATEGORY[ptcType]
                    )
                    if(isEmpty(matchingPtcFare)) {
                        return false
                    }
                }

                 // Layover Filter
                 const layoverAirportsSet = new Set()
                    Object.values(layoverAirport?.selectedOptions || {}).forEach((layover: any) => {
                        (layover || []).forEach((airport: any) => {
                            const s = airport.length - 4
                            const IATA = airport.slice(s, s + 3).toUpperCase() // Extracting IATA code from airport name
                            layoverAirportsSet.add(IATA)
                        })
                    }, 0)
                if (layoverAirportsSet.size) {
                    let hasFilteredLayover = false
                    const anyCard: any = card as any
                    if(isEmpty(anyCard?.layover)) {
                        hasFilteredLayover =true
                    }
                    if(!hasFilteredLayover) {
                        for (const layover of (anyCard?.layover || [])) {
                            const { airport } = layover || {}
                            if (layoverAirportsSet.has(airport)) {
                                hasFilteredLayover = true
                                break
                            }
                        }
                    }
                    
                    if (!hasFilteredLayover) return false
                }
                 
                // NOTE: Total round trip price filter is handled at a higher level
                // since it requires combining prices from both onward and return journeys
                return isCardValid
            })
        } catch (ex) {
            return cards
        }
    }

    const getFilteredCards = (cards: Cards, selectedFilters: any) => {
        return listOfKeys.map((journeyKey, index) => {
            const listOfCardsInJourney = cards?.[journeyKey] || []

            if (!isEmpty(listOfCardsInJourney)) {
                return getFilteredCardsForJourney(listOfCardsInJourney, index, selectedFilters)
            }
            return []
        })
    }

    // Apply global filters that affect the entire trip (not per-journey)
    const applyGlobalFilters = (filteredCardsByJourney: Card[][], selectedFilters: any) => {
        const { ctUpgrade, visaRejection } = selectedFilters || {}
        
        if (!ctUpgrade?.checked && !visaRejection?.checked) {
            return filteredCardsByJourney
        }
        if (ctUpgrade?.checked) {
            return filteredCardsByJourney.map((journeyCards) => {
                return journeyCards.filter(card => {
                    const faresObjectList = getFaresInfo(card, subTravelOptions, fares) || []
                    const cheapestFare = faresObjectList.find((fare: any) => fare?.isCheapest)
                    const benefitTags = cheapestFare?.benefitTags || []
                    
                    return benefitTags.some((tag: any) => tag?.benefitType === 'CT_UPGRADE')
                })
            })
        }
        if (visaRejection?.checked) {
            return filteredCardsByJourney.map((journeyCards) => {
                return journeyCards.filter(card => {
                    const faresObjectList = getFaresInfo(card, subTravelOptions, fares) || []
                    const cheapestFare = faresObjectList.find((fare: any) => fare?.isCheapest)
                    const benefitTags = cheapestFare?.benefitTags || []
                    
                    return benefitTags.some((tag: any) => tag?.benefitType === 'VISA_REJECTION_COVER')
                })
            })
        }
        return filteredCardsByJourney
    }

    const filteredCards = useMemo(() => {
        if (isStoreDataLoading || isEmpty(cards)) {
            return []
        }
        const perJourneyFilteredCards = getFilteredCards(cards, selectedFilters)
        return applyGlobalFilters(perJourneyFilteredCards, selectedFilters)
    }, [selectedFilters, cards, isStoreDataLoading])

    const cheapestFilteredFlight = useMemo(() => {
        try {
            const { onwardLeg: onwardLegFilters, returnLeg: returnLegFilters } = selectedFilters || {}
            const { ptcFare: onwardPtcFare } = onwardLegFilters || {}
            const { ptcFare: returnPtcFare } = returnLegFilters || {}
            const cheapestFares = filteredCards.map((cards, index) => {
                const showCheckInBaggagePrice = index === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
                const activePtcFare = index === 0 ? onwardPtcFare : returnPtcFare
                const ptcFareType = resolvePtcFareType(activePtcFare)
                const isPtcFilterApplied = !!ptcFareType
                return cards.reduce(
                    (acc, card) => {
                        const faresObjectList: FareType[] = getFaresInfo(card, subTravelOptions, fares, false, showCheckInBaggagePrice, ptcFareType) || []
                        const cheapestFare = isPtcFilterApplied ? faresObjectList?.[0] : faresObjectList.find(fare => fare.isCheapest)
                        const { totalPrice = 0 } = cheapestFare?.pricing?.perAdultPricing || {}
                        const { totalDuration } = card?.summary || {}

                        if (!acc.minPrice || acc.minPrice > totalPrice) {
                            acc.minPrice = totalPrice
                            acc.duration = totalDuration
                        } else if (acc.minPrice === totalPrice) {
                            const accTotalMinutes = (acc.duration.hh || 0) * 60 + (acc.duration.mm || 0)
                            const cardTotalMinutes = (totalDuration.hh || 0) * 60 + (totalDuration.mm || 0)
                            if (cardTotalMinutes < accTotalMinutes) {
                                acc.duration = totalDuration
                            }
                        }

                        return acc
                    },
                    { minPrice: 0, duration: { hh: 0, mm: 0 } }
                )
            })

            const [onwardCheapestFare, returnCheapestFare] = cheapestFares
            const totalPrice = (onwardCheapestFare.minPrice || 0) + (returnCheapestFare?.minPrice || 0)
            if (totalPrice === 0) {
                return ''
            }
            let totalHours = (onwardCheapestFare?.duration?.hh || 0) + (returnCheapestFare?.duration?.hh || 0)
            let totalMinutes = (onwardCheapestFare?.duration?.mm || 0) + (returnCheapestFare?.duration?.mm || 0)
            if (totalMinutes >= 60) {
                totalHours += Math.floor(totalMinutes / 60)
                totalMinutes = totalMinutes % 60
            }
            const flightDuration = `${totalHours ? `${totalHours}h` : ''} ${totalMinutes ? `${totalMinutes}m` : ''}`
            return `${formatCurrency(totalPrice)} · ${flightDuration}`
        } catch (ex) {
            return ''
        }
    }, [filteredCards])

    const fastestFilteredFlight = useMemo(() => {
        try {
            if (isEmpty(filteredCards)) return ''
            const { onwardLeg: onwardLegFilters, returnLeg: returnLegFilters } = selectedFilters || {}
            const { ptcFare: onwardPtcFare } = onwardLegFilters || {}
            const { ptcFare: returnPtcFare } = returnLegFilters || {}
            // For each leg, pick the card with minimum totalDuration; use its cheapest fare price
            const fastestPerLeg = filteredCards.map((cards, index) => {
                const showCheckInBaggagePrice = index === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
                const activePtcFare = index === 0 ? onwardPtcFare : returnPtcFare
                const ptcFareType = resolvePtcFareType(activePtcFare)
                const isPtcFilterApplied = !!ptcFareType
                if (isEmpty(cards)) return { price: 0, duration: { hh: 0, mm: 0 } }
                let best: { price: number; duration: { hh: number; mm: number } } | null = null
                cards.forEach(card => {
                    const faresObjectList: FareType[] = getFaresInfo(card, subTravelOptions, fares, false, showCheckInBaggagePrice, ptcFareType) || []
                    const cheapestFare = isPtcFilterApplied ? faresObjectList?.[0] : faresObjectList.find(fare => fare.isCheapest)
                    const { totalPrice = 0 } = cheapestFare?.pricing?.perAdultPricing || {}
                    const { totalDuration = { hh: 0, mm: 0 } } = card?.summary || {}
                    const cardMinutes = (totalDuration?.hh || 0) * 60 + (totalDuration?.mm || 0)
                    const bestMinutes = best
                        ? (best?.duration?.hh || 0) * 60 + (best?.duration?.mm || 0)
                        : Infinity
                    if (cardMinutes < bestMinutes || (cardMinutes === bestMinutes && best && totalPrice < best?.price)) {
                        best = { price: totalPrice, duration: totalDuration }
                    }
                })
                return best || { price: 0, duration: { hh: 0, mm: 0 } }
            })

            const totalPrice = fastestPerLeg.reduce((sum, item: any) => sum + (item.price || 0), 0)
            if (totalPrice === 0) return ''
            let totalHours = fastestPerLeg.reduce((sum, item: any) => sum + (item?.duration?.hh || 0), 0)
            let totalMinutes = fastestPerLeg.reduce((sum, item: any) => sum + (item?.duration?.mm || 0), 0)
            if (totalMinutes >= 60) {
                totalHours += Math.floor(totalMinutes / 60)
                totalMinutes = totalMinutes % 60
            }
            const flightDuration = `${totalHours ? `${totalHours}h` : ''} ${totalMinutes ? `${totalMinutes}m` : ''}`
            return `${formatCurrency(totalPrice)} · ${flightDuration}`
        } catch (ex) {
            return ''
        }
    }, [filteredCards])

    const totalFlightsCount = (cards?.J1?.length || 0) + (cards?.J2?.length || 0)
    const filteredFlightsCount = (filteredCards?.[0]?.length || 0) + (filteredCards?.[1]?.length || 0)
    const onewayFilteredFlightsCount = (filteredCards?.[0]?.length || 0)

    return { filteredCards, totalFlightsCount, filteredFlightsCount, onewayFilteredFlightsCount, cheapestFilteredFlight, fastestFilteredFlight }
}

export default useFilteredCards
